import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowUpRight, BadgeCheck, Bell, Check, Download, Flame, Loader2, Lock,
  MessageCircle, Package, ShieldCheck, Users, X, Zap,
} from "lucide-react";
import { AeroNav } from "@/components/aero/AeroNav";
import { AeroFooter } from "@/components/aero/AeroFooter";
import { WhatsAppFloat } from "@/components/cine/WhatsAppFloat";
import { SOCIALS } from "@/constants/content";
import { useAuth } from "@/hooks/useAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const loadRazorpay = () =>
  new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });

const inputDark =
  "w-full bg-white/[0.06] border border-white/15 focus:border-[#C9A227] outline-none rounded-2xl px-5 py-4 text-sm text-white placeholder:text-white/35 transition-colors";

export default function ProductDetailPage() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [waBase, setWaBase] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "" });
  const [paying, setPaying] = useState(false);
  const [buyDone, setBuyDone] = useState(false);
  const [dlUrl, setDlUrl] = useState(null);
  const pollRef = useRef(null);

  const fetchProduct = useCallback(() => {
    axios
      .get(`${API}/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
    axios
      .get(`${API}/config`)
      .then((r) => r.data.whatsapp_number && setWaBase(r.data.whatsapp_number))
      .catch(() => {});
    pollRef.current = setInterval(fetchProduct, 15000);
    return () => clearInterval(pollRef.current);
  }, [fetchProduct]);

  const eb = product?.early_bird;
  const ebActive = eb && eb.remaining > 0;
  const effectivePrice = ebActive ? eb.early_price : product?.price;
  const live = product?.status === "live";
  const physical = product?.category === "physical";

  const openBuy = () => {
    if (!user) {
      toast.error("Purchase ke liye pehle Google se sign in karo");
      login();
      return;
    }
    setBuyer({ name: user.name || "", email: user.email || "", phone: "" });
    setBuyDone(false);
    setDlUrl(null);
    setBuyOpen(true);
  };

  const pay = async () => {
    if (buyer.name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(buyer.email)) {
      toast.error("Naam aur valid email daalo — download link isi par jayega");
      return;
    }
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("rzp load failed");
      const { data } = await axios.post(`${API}/product-orders/create-order`, {
        product_id: product.id,
        name: buyer.name.trim(),
        email: buyer.email.trim(),
        phone: buyer.phone.trim(),
      });
      const rzp = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        amount: data.amount,
        currency: "INR",
        name: "FILMMAKERAKKI",
        description: product.name,
        prefill: { name: buyer.name, email: buyer.email, contact: buyer.phone },
        theme: { color: "#0A0908" },
        handler: async (resp) => {
          try {
            const vres = await axios.post(`${API}/product-orders/verify`, {
              booking_id: data.purchase_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setDlUrl(vres.data?.download_url || null);
            setBuyDone(true);
            fetchProduct();
          } catch {
            toast.error("Payment verify nahi hua — screenextfilms@gmail.com par contact karo.");
          }
        },
        modal: { ondismiss: () => toast.error("Payment cancel hua — order complete nahi hua.") },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment start nahi ho paya — dobara try karo.");
    } finally {
      setPaying(false);
    }
  };

  const contactHref = waBase
    ? `https://wa.me/${waBase}?text=${encodeURIComponent(`Hi Akhilesh, mujhe "${product?.name}" order karna hai. Details share karo.`)}`
    : SOCIALS.instagram;

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen bg-[#0A0908] text-white overflow-x-clip"
      data-testid="product-detail-page"
    >
      <AeroNav />

      {/* ambient glows */}
      <div aria-hidden className="pointer-events-none absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#C9A227]/[0.08] blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#FF7A1A]/[0.06] blur-3xl" />

      <section className="relative z-10 pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Link
            to="/products"
            data-testid="pd-back"
            className="inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-white/50 hover:text-[#F5C04E] transition-colors"
          >
            <ArrowLeft size={13} /> Back to Store
          </Link>

          {notFound ? (
            <div className="mt-20 text-center" data-testid="pd-not-found">
              <p className="font-serif text-4xl text-white/80">Product nahi mila</p>
              <Link to="/products" className="mt-6 inline-flex items-center gap-2 text-[#C9A227] font-mono text-[11px] font-bold tracking-[0.2em] uppercase">
                Store par wapas jao <ArrowUpRight size={13} />
              </Link>
            </div>
          ) : !product ? (
            <div className="mt-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start animate-pulse" data-testid="pd-skeleton">
              <div className="mx-auto w-full max-w-[440px] aspect-[4/5] rounded-[20px] bg-white/[0.06] border border-white/10" />
              <div>
                <div className="h-4 w-56 rounded-lg bg-white/10" />
                <div className="mt-5 h-12 w-4/5 rounded-2xl bg-white/10" />
                <div className="mt-4 h-4 w-full max-w-lg rounded-lg bg-white/[0.07]" />
                <div className="mt-2 h-4 w-2/3 max-w-lg rounded-lg bg-white/[0.07]" />
                <div className="mt-8 rounded-[22px] border border-white/10 bg-white/[0.03] p-7">
                  <div className="h-7 w-64 rounded-full bg-[#C9A227]/15" />
                  <div className="mt-4 h-14 w-40 rounded-2xl bg-white/10" />
                  <div className="mt-5 h-2 w-full rounded-full bg-white/10" />
                  <div className="mt-6 h-12 w-52 rounded-full bg-[#C9A227]/20" />
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* cover */}
              <motion.div
                initial={{ opacity: 0, y: 30, rotate: 1.5 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                transition={{ duration: 0.9, ease }}
                className="relative mx-auto w-full max-w-[440px]"
              >
                <div aria-hidden className="absolute -inset-5 rounded-[32px] bg-gradient-to-br from-[#C9A227]/30 to-[#FF7A1A]/15 blur-2xl" />
                {product.thumbnail_url ? (
                  <img
                    src={product.thumbnail_url}
                    alt={`${product.name} — cover`}
                    data-testid="pd-cover"
                    className="relative w-full rounded-[20px] border border-[#C9A227]/25 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]"
                  />
                ) : (
                  <div data-testid="pd-cover" className="relative w-full aspect-[4/5] rounded-[20px] border border-white/10 bg-gradient-to-br from-[#141210] to-[#0A0908] flex items-center justify-center">
                    <Package size={72} strokeWidth={0.8} className="text-[#C9A227]/50" />
                  </div>
                )}
                {ebActive && (
                  <span className="absolute -top-4 -right-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908] font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-4 py-2.5 rounded-full shadow-lg rotate-3">
                    <Flame size={11} /> Early Bird
                  </span>
                )}
              </motion.div>

              {/* details */}
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease }}>
                <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-[#C9A227] flex items-center gap-3">
                  <span className="w-8 h-px bg-[#C9A227]/60 inline-block" />
                  {physical ? "Physical Product" : "Digital Download"} · FilmmakerAkki
                </p>
                <h1 className="mt-4 font-display font-black text-4xl md:text-[52px] leading-[1.03] tracking-tight" data-testid="pd-name">
                  {product.name}
                </h1>
                <p className="mt-5 text-white/60 leading-relaxed max-w-lg" data-testid="pd-description">{product.description}</p>

                {/* price block */}
                <div className="mt-8 rounded-[22px] border border-[#C9A227]/25 bg-white/[0.03] p-6 md:p-7" data-testid="pd-price-block">
                  {ebActive && (
                    <p data-testid="pd-urgency" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C9A227]/20 to-[#FF7A1A]/15 border border-[#C9A227]/40 text-[#F5C04E] font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full">
                      <Zap size={11} /> Early Bird Offer — Limited to First {eb.limit} Buyers Only
                    </p>
                  )}
                  <div className="mt-4 flex items-end gap-4 flex-wrap">
                    <p data-testid="pd-price-early" className="font-display font-black text-5xl md:text-6xl bg-gradient-to-r from-[#C9A227] via-[#F5C04E] to-[#FF7A1A] bg-clip-text text-transparent">
                      ₹{Number(effectivePrice).toLocaleString("en-IN")}
                    </p>
                    {ebActive && (
                      <p data-testid="pd-price-regular" className="font-display font-bold text-2xl text-white/35 line-through mb-1.5">
                        ₹{Number(product.price).toLocaleString("en-IN")}
                      </p>
                    )}
                  </div>
                  {eb && (
                    <div className="mt-5">
                      <div className="flex items-center justify-between gap-4">
                        <p data-testid="pd-counter" className="font-mono text-[11px] font-bold tracking-[0.15em] uppercase text-white/75 flex items-center gap-2">
                          <Users size={13} className="text-[#C9A227]" />
                          {ebActive ? `${eb.remaining} of ${eb.limit} early bird spots remaining` : "Early bird sold out — regular price ab lagega"}
                        </p>
                        <p className="font-mono text-[10px] text-white/40">{eb.sold} sold</p>
                      </div>
                      <div className="mt-2.5 h-2 rounded-full bg-white/10 overflow-hidden" data-testid="pd-progress">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, (eb.sold / eb.limit) * 100)}%` }}
                          transition={{ duration: 1, ease }}
                          className="h-full rounded-full bg-gradient-to-r from-[#C9A227] to-[#FF7A1A]"
                        />
                      </div>
                    </div>
                  )}
                  {physical && product.stock != null && (
                    <p className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">{product.stock} in stock</p>
                  )}

                  {/* CTA */}
                  <div className="mt-7">
                    {!live ? (
                      <span data-testid="pd-coming-soon" className="inline-flex items-center gap-2 border border-[#C9A227]/50 bg-[#C9A227]/10 text-[#F5C04E] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full cursor-default">
                        <Bell size={13} /> Coming Soon
                      </span>
                    ) : physical ? (
                      <a
                        data-testid="pd-buy"
                        href={contactHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2.5 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908] font-mono text-[11px] font-bold tracking-[0.22em] uppercase px-9 py-4 rounded-full hover:shadow-[0_15px_40px_-10px_rgba(201,162,39,0.6)] hover:-translate-y-0.5 transition-all duration-300"
                      >
                        <MessageCircle size={14} /> Contact to Order
                      </a>
                    ) : (
                      <button
                        data-testid="pd-buy"
                        onClick={openBuy}
                        className="inline-flex items-center gap-2.5 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908] font-mono text-[11px] font-bold tracking-[0.22em] uppercase px-9 py-4 rounded-full hover:shadow-[0_15px_40px_-10px_rgba(201,162,39,0.6)] hover:-translate-y-0.5 transition-all duration-300"
                      >
                        <Lock size={13} /> Buy Now — ₹{Number(effectivePrice).toLocaleString("en-IN")}
                      </button>
                    )}
                  </div>
                </div>

                {/* trust chips */}
                <div className="mt-7 flex flex-wrap gap-x-7 gap-y-3">
                  {[
                    { icon: Download, t: "Instant Email Delivery" },
                    { icon: ShieldCheck, t: "Secure Razorpay Checkout" },
                    { icon: BadgeCheck, t: "Made From Real Shoots" },
                  ].map((x) => {
                    const Icon = x.icon;
                    return (
                      <p key={x.t} className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/45 flex items-center gap-2">
                        <Icon size={13} className="text-[#C9A227]" /> {x.t}
                      </p>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </section>

      {/* Buy modal (Razorpay) — dark */}
      <AnimatePresence>
        {buyOpen && product && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center px-5"
            onClick={() => !paying && setBuyOpen(false)}
            data-testid="pd-buy-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.35, ease }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-[#111009] border border-[#C9A227]/25 rounded-[26px] p-8 md:p-10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.9)]"
            >
              <button
                onClick={() => !paying && setBuyOpen(false)}
                aria-label="Close"
                data-testid="pd-buy-close"
                className="absolute top-5 right-5 w-9 h-9 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-white/40 flex items-center justify-center transition-colors"
              >
                <X size={15} />
              </button>
              {buyDone ? (
                <div className="text-center py-4" data-testid="pd-buy-success">
                  <span className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-[#C9A227] to-[#FF7A1A] flex items-center justify-center">
                    <Check size={28} strokeWidth={2.5} className="text-[#0A0908]" />
                  </span>
                  <h3 className="mt-6 font-serif text-3xl text-white">Payment Successful!</h3>
                  <p className="mt-3 text-sm text-white/55 leading-relaxed">
                    <strong className="text-white">{product.name}</strong> ka order confirm ho gaya. Download link tumhare email <strong className="text-white">{buyer.email}</strong> par bheja jayega.
                  </p>
                  {dlUrl && (
                    <div className="mt-6" data-testid="pd-instant-download">
                      <a
                        href={dlUrl}
                        data-testid="pd-download-now"
                        className="inline-flex items-center gap-2.5 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908] font-mono text-[11px] font-bold tracking-[0.22em] uppercase px-9 py-4 rounded-full hover:shadow-[0_15px_40px_-10px_rgba(201,162,39,0.6)] hover:-translate-y-0.5 transition-all duration-300"
                      >
                        <Download size={14} /> Download Now
                      </a>
                      <p className="mt-3 font-mono text-[9px] tracking-[0.15em] uppercase text-white/35">Secure one-time link · 24 hours valid · sirf tumhare email ke saath kaam karegi</p>
                    </div>
                  )}
                  <div className="mt-7 flex flex-wrap justify-center gap-4">
                    <Link
                      to="/my-orders"
                      data-testid="pd-buy-success-my-orders"
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full"
                    >
                      <Download size={13} /> My Orders
                    </Link>
                    <button
                      data-testid="pd-buy-done-close"
                      onClick={() => setBuyOpen(false)}
                      className="inline-flex items-center gap-2 border border-white/15 text-white/60 font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:text-white transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#C9A227]">Secure Checkout</p>
                  <h3 className="mt-3 font-display font-bold text-2xl text-white leading-tight">{product.name}</h3>
                  <div className="mt-1.5 flex items-end gap-3">
                    <p className="font-display font-black text-3xl text-[#F5C04E]" data-testid="pd-modal-price">₹{Number(effectivePrice).toLocaleString("en-IN")}</p>
                    {ebActive && <p className="font-display font-bold text-lg text-white/30 line-through mb-0.5">₹{Number(product.price).toLocaleString("en-IN")}</p>}
                  </div>
                  {ebActive && (
                    <p className="mt-2 font-mono text-[9px] tracking-[0.18em] uppercase text-[#C9A227]/80">{eb.remaining} of {eb.limit} early bird spots remaining</p>
                  )}
                  <div className="mt-6 space-y-3">
                    <input value={buyer.name} onChange={(e) => setBuyer((p) => ({ ...p, name: e.target.value }))} placeholder="Full Name" data-testid="pd-buy-name" className={inputDark} />
                    <input type="email" value={buyer.email} readOnly placeholder="Email — download link isi par jayega" data-testid="pd-buy-email" className={`${inputDark} opacity-70 cursor-not-allowed`} />
                    <input value={buyer.phone} onChange={(e) => setBuyer((p) => ({ ...p, phone: e.target.value }))} placeholder="Phone (optional)" data-testid="pd-buy-phone" className={inputDark} />
                  </div>
                  <button
                    onClick={pay}
                    disabled={paying}
                    data-testid="pd-buy-pay"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#C9A227] to-[#FF7A1A] text-[#0A0908] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:shadow-[0_15px_40px_-10px_rgba(201,162,39,0.6)] transition-all duration-300 disabled:opacity-60"
                  >
                    {paying ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />}
                    {paying ? "Opening Razorpay…" : `Pay ₹${Number(effectivePrice).toLocaleString("en-IN")} Securely`}
                  </button>
                  <p className="mt-4 flex items-center justify-center gap-2 font-mono text-[8px] tracking-[0.2em] uppercase text-white/35">
                    <ShieldCheck size={11} className="text-[#C9A227]" /> Razorpay Secure · UPI · Cards · NetBanking
                  </p>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AeroFooter />
      <WhatsAppFloat />
    </motion.main>
  );
}
